import { useRef, useState, useCallback } from 'react';
/**
 * Pure display component for gallery groups (w=3 or w=4 image blocks).
 * Desktop: horizontal scrollable track with drag support.
 * Mobile: scroll + dot indicators.
 * No editing UI. Safe to copy to frontend.
 */
export function GallerySliderView({ slides }) {
    const validSlides = slides.filter(s => s.url);
    const [activeIndex, setActiveIndex] = useState(0);
    const trackRef = useRef(null);
    const isProgrammaticScroll = useRef(false);
    const dragRef = useRef(null);
    const handleScroll = useCallback(() => {
        if (isProgrammaticScroll.current)
            return;
        const el = trackRef.current;
        if (!el || validSlides.length === 0)
            return;
        const gap = 16;
        const cardWidth = (el.scrollWidth - (validSlides.length - 1) * gap) / validSlides.length;
        const index = Math.round(el.scrollLeft / (cardWidth + gap));
        setActiveIndex(Math.max(0, Math.min(index, validSlides.length - 1)));
    }, [validSlides.length]);
    const goTo = (index) => {
        const el = trackRef.current;
        if (!el)
            return;
        const gap = 16;
        const cardWidth = (el.scrollWidth - (validSlides.length - 1) * gap) / validSlides.length;
        isProgrammaticScroll.current = true;
        el.scrollTo({ left: index * (cardWidth + gap), behavior: 'smooth' });
        setActiveIndex(index);
        setTimeout(() => { isProgrammaticScroll.current = false; }, 500);
    };
    const handleMouseDown = (e) => {
        const el = trackRef.current;
        if (!el)
            return;
        dragRef.current = { startX: e.pageX - el.offsetLeft, scrollLeft: el.scrollLeft };
        el.style.cursor = 'grabbing';
        el.style.userSelect = 'none';
    };
    const handleMouseMove = (e) => {
        if (!dragRef.current)
            return;
        const el = trackRef.current;
        if (!el)
            return;
        el.scrollLeft = dragRef.current.scrollLeft - (e.pageX - el.offsetLeft - dragRef.current.startX);
    };
    const handleMouseUp = () => {
        const el = trackRef.current;
        if (el) {
            el.style.cursor = '';
            el.style.userSelect = '';
        }
        dragRef.current = null;
    };
    if (validSlides.length === 0)
        return null;
    return (<div className="tpl3-image-slider">
      <div ref={trackRef} className="tpl3-image-slider-track" onScroll={handleScroll} onMouseDown={handleMouseDown} onMouseMove={handleMouseMove} onMouseUp={handleMouseUp} onMouseLeave={handleMouseUp}>
        {validSlides.map((slide) => (<div key={slide.id} className="tpl3-image-slider-card">
            <img src={slide.url} alt={slide.caption || `Gallery image`} className="w-full h-full object-cover rounded-[8px]" loading="lazy"/>
            {slide.caption && (<p className="text-sm text-center text-[#44413D] mt-2">{slide.caption}</p>)}
          </div>))}
      </div>
      {validSlides.length > 1 && (<div className="tpl3-image-slider-dots">
          {validSlides.map((_, i) => (<button key={i} type="button" className={`tpl3-image-slider-dot ${i === activeIndex ? 'is-active' : ''}`} aria-label={`第 ${i + 1} 張`} onClick={() => goTo(i)}/>))}
        </div>)}
    </div>);
}
